import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Stack,
  SegmentedControl,
  Textarea,
  TextInput,
  FileInput,
  Loader,
  Text,
  Box,
  Button,
  Group,
} from '@mantine/core';
import { IconCamera, IconUpload } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { isEqual } from 'lodash';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { CodeableConceptInput } from '@/components/medplum-react/CodeableConceptInput/CodeableConceptInput';
import { DocumentScannerModal } from '@/components/DocumentScanner/DocumentScannerModal';
import { ARKON } from '@/consts/systems.consts';
import { ReportModalProps, ReportFormState } from './utils/report-files.types';
import {
  REPORT_CATEGORY_OPTIONS,
  REPORT_RESULT_OPTIONS,
  ReportCategoryValue,
  ReportResultValue,
} from './utils/report-files.consts';
import {
  createInitialReportFormState,
  getModalityOptions,
  hasCategorySpecificData,
  isValidDate,
  validateFileForUpload,
  saveDiagnosticReport,
} from './utils/report-files.utils';
import { useReportFileModal } from './utils/useReportFileModal';

export const ReportFileModal: FC<ReportModalProps> = ({
  opened,
  onClose,
  patient,
  encounter,
  onSuccess,
  report,
}) => {
  const { medplum, t, formState, setFormState } = useReportFileModal({
    opened,
    onClose,
    patient,
    encounter,
    report,
  });

  const [saving, setSaving] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [scannerModalOpened, setScannerModalOpened] = useState(false);

  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<ReportFormState | null>(null);
  const [baselineSet, setBaselineSet] = useState(false);

  useEffect(() => {
    if (opened) {
      setBaselineSet(true);
    } else {
      baselineRef.current = null;
      setBaselineSet(false);
    }
  }, [opened]);

  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { ...(formState as ReportFormState) };
      setBaselineSet(false);
    }
  }, [baselineSet, formState, opened]);

  const fileValidation = validateFileForUpload(formState.file);
  const hasValidDate = isValidDate(formState.date);
  const hasCategoryData = hasCategorySpecificData(formState);
  const isFormValid = hasValidDate && hasCategoryData;

  const modalityOptions = useMemo(
    () => getModalityOptions(formState.investigationType),
    [formState.investigationType]
  );

  const currentFileName = report?.presentedForm?.[0]?.title;

  const setCategory = (category: ReportCategoryValue) => {
    setFormState((prev) => ({
      ...prev,
      category,
      geneticTestCode:
        category === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC
          ? prev.geneticTestCode
          : null,
      geneticTestDisplay:
        category === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC
          ? prev.geneticTestDisplay
          : '',
      investigationType:
        category === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION
          ? prev.investigationType
          : null,
      investigationModality:
        category === ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION
          ? prev.investigationModality
          : null,
      biopsyType:
        category === ARKON.CS.REPORTS.CATEGORY.CODES.BIOPSY
          ? prev.biopsyType
          : '',
    }));
  };

  const handleFileSelect = (file: File | null) => {
    setUploadingFile(true);
    setFormState((prev) => ({ ...prev, file }));
    setUploadingFile(false);
  };

  const handleScanComplete = (scannedFile: File) => {
    setFormState((prev) => ({ ...prev, file: scannedFile }));
    setScannerModalOpened(false);
  };

  const hasUnsavedChanges = useMemo(() => {
    if (!baselineRef.current) {
      return false;
    }
    try {
      return !isEqual(formState, baselineRef.current);
    } catch {
      return true;
    }
  }, [formState]);

  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setFormState(baselineRef.current);
      return;
    }
    setFormState(createInitialReportFormState(encounter));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!isFormValid || saving) {
      return;
    }

    setSaving(true);
    try {
      await saveDiagnosticReport({
        medplum,
        patient,
        encounterId: encounter?.id,
        formState,
        existingReport: report || undefined,
      });

      showNotification({
        color: 'green',
        title: t('reportModal.saveSuccessTitle', {
          ns: 'patient-pages',
          defaultValue: 'Επιτυχής αποθήκευση',
        }),
        message: report
          ? t('reportModal.saveSuccessMessage', {
              ns: 'patient-pages',
              defaultValue: 'Το διαγνωστικό report ενημερώθηκε.',
            })
          : t('reportModal.createSuccessMessage', {
              ns: 'patient-pages',
              defaultValue: 'Το διαγνωστικό report δημιουργήθηκε.',
            }),
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save diagnostic report', error);
      showNotification({
        color: 'red',
        title: t('reportModal.saveErrorTitle', {
          ns: 'patient-pages',
          defaultValue: 'Σφάλμα αποθήκευσης',
        }),
        message:
          error instanceof Error
            ? error.message
            : t('reportModal.saveErrorMessage', {
                ns: 'patient-pages',
                defaultValue: 'Παρουσιάστηκε σφάλμα. Προσπαθήστε ξανά.',
              }),
      });
    } finally {
      setSaving(false);
    }
  };

  const CATEGORY_SEGMENT_OPTIONS = REPORT_CATEGORY_OPTIONS.map((option) => ({
    value: option.value,
    label: t(`reportCards.categoryOptions.${option.label}`, {
      ns: 'patient-pages',
      defaultValue: option.label,
    }),
  }));

  const RESULT_SEGMENT_OPTIONS = REPORT_RESULT_OPTIONS.map((option) => ({
    value: option.value,
    label: t(`reportCards.resultOptions.${option.label}`, {
      ns: 'patient-pages',
      defaultValue: option.label,
    }),
  }));

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      title={
        report
          ? t('reportModal.editTitle', {
              ns: 'patient-pages',
              defaultValue: 'Επεξεργασία διαγνωστικού report',
            })
          : t('reportModal.createTitle', {
              ns: 'patient-pages',
              defaultValue: 'Νέο διαγνωστικό report',
            })
      }
      size="xl"
      closeOnClickOutside={false}
      closeOnEscape={false}
    >
      <Stack gap="md">
        <SegmentedControl
          fullWidth
          data={CATEGORY_SEGMENT_OPTIONS}
          value={formState.category}
          onChange={(value) => setCategory(value as ReportCategoryValue)}
        />

        {formState.category === ARKON.CS.REPORTS.CATEGORY.CODES.GENETIC && (
          <CodeableConceptInput
            key={`genetic-${formState.geneticTestCode ?? 'new'}`}
            path="DiagnosticReport.code"
            binding={ARKON.VS.GENETIC_TESTS_EL}
            label={t('reportModal.geneticTestLabel', {
              ns: 'patient-pages',
              defaultValue: 'Επιλέξτε γενετική εξέταση',
            })}
            name="geneticTest"
            maxValues={1}
            loadAll
            defaultValue={
              formState.geneticTestCode
                ? {
                    coding: [
                      {
                        system: ARKON.CS.GENETIC_TESTS.BASE,
                        code: formState.geneticTestCode,
                        display: formState.geneticTestDisplay,
                      },
                    ],
                  }
                : undefined
            }
            onChange={(value) => {
              const coding = value?.coding?.[0];
              setFormState((prev) => ({
                ...prev,
                geneticTestCode: coding?.code || null,
                geneticTestDisplay: coding?.display || '',
              }));
            }}
          />
        )}

        {formState.category ===
          ARKON.CS.REPORTS.CATEGORY.CODES.INVESTIGATION && (
          <>
            <CodeableConceptInput
              key={`investigation-${formState.investigationType?.coding?.[0]?.code ?? 'new'}`}
              path="DiagnosticReport.code"
              binding={ARKON.VS.REPORT_FILE_TYPE_EL}
              label={t('reportModal.investigationLabel', {
                ns: 'patient-pages',
                defaultValue: 'Επιλέξτε διερεύνηση',
              })}
              name="investigationType"
              maxValues={1}
              loadAll
              defaultValue={formState.investigationType || undefined}
              onChange={(value) => {
                setFormState((prev) => ({
                  ...prev,
                  investigationType: value || null,
                  investigationModality: null,
                }));
              }}
            />
            {modalityOptions && (
              <Box>
                <Text size="sm" fw={500} mt={3}>
                  {t('reportModal.modalityLabel', {
                    ns: 'patient-pages',
                    defaultValue: 'Μέθοδος',
                  })}
                </Text>
                <SegmentedControl
                  fullWidth
                  data={[...modalityOptions]}
                  value={formState.investigationModality || undefined}
                  onChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      investigationModality: (value as string) || null,
                    }))
                  }
                />
              </Box>
            )}
          </>
        )}

        {formState.category === ARKON.CS.REPORTS.CATEGORY.CODES.BIOPSY && (
          <TextInput
            label={t('reportModal.biopsyLabel', {
              ns: 'patient-pages',
              defaultValue: 'Τύπος βιοψίας',
            })}
            placeholder={t('reportModal.biopsyPlaceholder', {
              ns: 'patient-pages',
              defaultValue: 'Περιγράψτε τον τύπο της βιοψίας',
            })}
            value={formState.biopsyType}
            onChange={(event) =>
              setFormState((prev) => ({
                ...prev,
                biopsyType: event.currentTarget.value,
              }))
            }
          />
        )}

        <DateInputWrapper
          label={t('reportModal.dateLabel', {
            ns: 'patient-pages',
            defaultValue: 'Ημερομηνία εξέτασης',
          })}
          value={formState.date ? new Date(formState.date) : null}
          onChange={(date) =>
            setFormState((prev) => ({
              ...prev,
              date: date ? date.toISOString().split('T')[0] : '',
            }))
          }
          maxDate={new Date()}
          required
          error={
            opened && !hasValidDate
              ? t('reportModal.invalidDate', {
                  ns: 'patient-pages',
                  defaultValue: 'Η ημερομηνία δεν μπορεί να είναι μελλοντική.',
                })
              : null
          }
        />

        <Box>
          <Text size="sm" fw={500} mt={3}>
            {t('reportModal.resultLabel', {
              ns: 'patient-pages',
              defaultValue: 'Αποτέλεσμα',
            })}
          </Text>
          <SegmentedControl
            fullWidth
            data={RESULT_SEGMENT_OPTIONS}
            value={formState.result || undefined}
            onChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                result: value ? (value as ReportResultValue) : null,
              }))
            }
          />
        </Box>

        <Textarea
          label={t('reportModal.commentsLabel', {
            ns: 'patient-pages',
            defaultValue: 'Σχόλια / συμπεράσματα',
          })}
          placeholder={t('reportModal.commentsPlaceholder', {
            ns: 'patient-pages',
            defaultValue: 'Ελεύθερο κείμενο για επιπλέον πληροφορίες',
          })}
          value={formState.comments}
          onChange={(event) =>
            setFormState((prev) => ({
              ...prev,
              comments: event.currentTarget.value,
            }))
          }
          minRows={4}
        />

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {t('reportModal.fileLabel', {
              ns: 'patient-pages',
              defaultValue: 'Αρχείο αποτελέσματος (PDF)',
            })}
          </Text>

          <Group gap="sm" grow>
            <Button
              variant="light"
              leftSection={<IconCamera size={18} />}
              onClick={() => setScannerModalOpened(true)}
              disabled={uploadingFile || saving}
            >
              {t('reportModal.scanDocument', {
                ns: 'patient-pages',
                defaultValue: 'Σάρωση εγγράφου',
              })}
            </Button>

            <FileInput
              placeholder={
                currentFileName ||
                t('reportModal.filePlaceholder', {
                  ns: 'patient-pages',
                  defaultValue: 'Επιλέξτε αρχείο',
                })
              }
              accept="application/pdf"
              value={formState.file}
              onChange={handleFileSelect}
              disabled={uploadingFile || saving}
              rightSection={uploadingFile ? <Loader size="sm" /> : null}
              leftSection={<IconUpload size={18} />}
            />
          </Group>

          {(formState.file && !fileValidation.isPdf) ||
          (formState.file && !fileValidation.isSizeValid) ? (
            <Text size="xs" c="red">
              {formState.file && !fileValidation.isPdf
                ? t('reportModal.fileTypeError', {
                    ns: 'patient-pages',
                    defaultValue: 'Το αρχείο πρέπει να είναι PDF.',
                  })
                : t('reportModal.fileSizeError', {
                    ns: 'patient-pages',
                    defaultValue:
                      'Το αρχείο υπερβαίνει το επιτρεπτό μέγεθος (10MB).',
                  })}
            </Text>
          ) : null}

          {formState.file && (
            <Text size="xs" c="dimmed">
              {formState.file.name} ({(formState.file.size / 1024 / 1024).toFixed(2)} MB)
            </Text>
          )}
        </Stack>

        <FormActionButtons
          ref={formActionsRef}
          onSave={handleSave}
          onDiscard={handleDiscardChanges}
          onClose={handleClose}
          hasUnsavedChanges={hasUnsavedChanges}
          isLoading={saving || uploadingFile}
          isFormValid={isFormValid}
          saveLabel={t('reportModal.saveCta', {
            ns: 'patient-pages',
            defaultValue: 'Αποθήκευση',
          })}
        />
      </Stack>

      {/* Document Scanner Modal */}
      <DocumentScannerModal
        opened={scannerModalOpened}
        onClose={() => setScannerModalOpened(false)}
        onScanComplete={handleScanComplete}
        preferredCamera="environment"
      />
    </Modal>
  );
};

export default ReportFileModal;
